import { Placeholder } from '@tiptap/extension-placeholder'
import { EditorContent, useEditor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { Bold, Italic, List, ListOrdered } from 'lucide-react'

interface RichTextEditorProps {
  readonly initialContent: string
  readonly onChange: (html: string) => void
  readonly placeholder?: string
  readonly minHeightClassName?: string
}

export const RichTextEditor = ({
  initialContent,
  onChange,
  placeholder = '',
  minHeightClassName = 'min-h-24',
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [StarterKit, Placeholder.configure({ placeholder })],
    content: initialContent,
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML()
      onChange(html === '<p></p>' ? '' : html)
    },
  })

  if (!editor) return null

  return (
    <div className="flex flex-col gap-0">
      <div className="flex items-center gap-1 rounded-t-md border border-border bg-secondary px-1 py-1">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('bold') ? 'bg-primary/10' : ''}`}
          aria-label="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('italic') ? 'bg-primary/10' : ''}`}
          aria-label="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('bulletList') ? 'bg-primary/10' : ''}`}
          aria-label="Bullet list"
        >
          <List className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`rounded p-1.5 transition hover:bg-primary/10 ${editor.isActive('orderedList') ? 'bg-primary/10' : ''}`}
          aria-label="Numbered list"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </button>
      </div>
      <EditorContent
        editor={editor}
        className={`${minHeightClassName} rounded-b-md border border-t-0 border-border bg-card px-3 py-2 text-sm text-primary focus-within:ring-1 focus-within:ring-primary/30 [&_.tiptap]:outline-none [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-4 [&_.tiptap_ul]:list-disc [&_.tiptap_ul]:pl-4 [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:text-primary/30 [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]`}
      />
    </div>
  )
}
